import { USER_WORKER_URL, WORKER_URL } from '../../../config.js';
import { fetchCache } from '../../../utils/fetchCache.js';
import { getSession } from '../../../utils/auth_utils.js';
import { storage } from '../../../utils/storage.js';
import { applySfwQueryParam } from '../../../utils/nsfwTags.js';

export const spriteSelectionKey = (charId, outfitId, actionId) =>
  `${charId}_${outfitId}_${actionId}`;

const writeLocalSpriteSelections = (characterId, outfitId, selectedKeys) => {
  const stored = storage.get('spriteGallerySelections') || {};
  const charKey = String(characterId);
  const outfitKey = String(outfitId);
  if (!stored[charKey]) stored[charKey] = {};
  stored[charKey][outfitKey] = Array.from(selectedKeys);
  storage.set('spriteGallerySelections', stored);
};

const parseActionIdFromSelectionKey = (key) => {
  const parts = String(key).split('_');
  if (parts.length < 3) return null;
  const id = parseInt(parts[2], 10);
  return Number.isInteger(id) ? id : null;
};

const fetchBackendActionIds = async (characterId, outfitId) => {
  try {
    const session = await getSession();
    if (!session?.access_token) return null;

    const searchParams = new URLSearchParams({
      character_id: String(Number(characterId)),
      outfit_id: String(Number(outfitId))
    });
    const data = await fetchCache.fetchJSON(
      `${USER_WORKER_URL.replace(/\/+$/, '')}/get-sprite-selections?${searchParams.toString()}`,
      {
        method: 'GET',
        skipCache: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        }
      }
    );
    if (!data?.success || !Array.isArray(data.action_ids)) return null;
    const ids = data.action_ids.map((id) => Number(id)).filter((id) => Number.isInteger(id));
    return ids.length > 0 ? ids : null;
  } catch (error) {
    console.warn('[spritePreferences] Failed to fetch selections:', error);
    return null;
  }
};

const fetchAllActionIdsForOutfit = async (characterId, outfitId) => {
  try {
    const searchParams = new URLSearchParams({
      character_id: String(characterId),
      outfit_id: String(outfitId)
    });
    applySfwQueryParam(searchParams);
    const payload = await fetchCache.fetchJSON(
      `${WORKER_URL.replace(/\/+$/, '')}/get_character_sprites?${searchParams.toString()}`,
      { skipCache: true }
    );
    const sprites = Array.isArray(payload?.sprites) ? payload.sprites : [];
    return sprites
      .map((sprite) => Number(sprite?.action_id))
      .filter((id) => Number.isInteger(id));
  } catch (error) {
    console.warn('[spritePreferences] Failed to fetch sprites:', error);
    return [];
  }
};

const saveActionIdsToBackend = async (characterId, outfitId, actionIds) => {
  try {
    const session = await getSession();
    if (!session?.access_token) return false;

    const data = await fetchCache.fetchJSON(
      `${USER_WORKER_URL.replace(/\/+$/, '')}/save-sprite-selections`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          character_id: Number(characterId),
          outfit_id: Number(outfitId),
          action_ids: actionIds
        })
      }
    );
    return data?.success !== false;
  } catch (error) {
    console.warn('[spritePreferences] Failed to save selections:', error);
    return false;
  }
};

/**
 * Remove one action from the user's (character, outfit) preference allowlist.
 * Empty prefs mean "all selected", so we materialize the full set minus this action.
 */
export const removeSpriteFromPreferences = async ({ characterId, outfitId, actionId }) => {
  const removedActionId = Number(actionId);
  if (!Number.isInteger(removedActionId)) return false;

  const stored = storage.get('spriteGallerySelections') || {};
  const charKey = String(characterId);
  const outfitKey = String(outfitId);
  const localKeys = stored[charKey]?.[outfitKey];
  const localIds = Array.isArray(localKeys)
    ? localKeys.map(parseActionIdFromSelectionKey).filter((id) => Number.isInteger(id))
    : [];

  let currentIds = localIds;
  if (currentIds.length === 0) {
    currentIds = (await fetchBackendActionIds(characterId, outfitId)) || [];
  }
  if (currentIds.length === 0) {
    currentIds = await fetchAllActionIdsForOutfit(characterId, outfitId);
  }

  const remainingIds = [...new Set(currentIds)].filter((id) => id !== removedActionId);
  const remainingKeys = remainingIds.map((id) => spriteSelectionKey(characterId, outfitId, id));
  writeLocalSpriteSelections(characterId, outfitId, remainingKeys);

  return saveActionIdsToBackend(characterId, outfitId, remainingIds);
};
